// The KGOB Journal — Blog Index (hero feature + card grid + archive teaser)

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ARTICLES as ALL_ARTICLES, CATEGORIES, YEARS, ARTICLE_BODIES } from './data';
import { Masthead, Colophon, Avatar, LevelPill, HeroArt, getJournalIssue } from './components';
import { useJsonLd } from '@/hooks/useJsonLd';
import './blog.css';

// Only show essays that have a real long-form body in ARTICLE_BODIES.
// Articles without a body remain in data.js for future content production
// but are hidden from the public index, archive, and sitemap.
const ARTICLES = (ALL_ARTICLES || []).filter((a) => a.bodyKey && ARTICLE_BODIES[a.bodyKey]);

function FeatureCard({ article, onOpen }) {
  return (
    <div className="feature" onClick={() => onOpen(article)} data-testid={`journal-feature-${article.id}`}>
      <div className="feature-art">
        <HeroArt kind={article.hero} article={article} />
      </div>
      <div className="feature-text">
        <div>
          <div className="feature-cat">{article.category} · The Lead Essay</div>
          <h1 className="feature-title">{article.title}</h1>
          <p className="feature-deck">{article.deck}</p>
        </div>
        <div className="feature-byline">
          <Avatar initials={article.author.initials} />
          <div>
            <div className="byline-name">{article.author.name}</div>
            <div className="byline-role">{article.author.role}</div>
          </div>
          <span className="feature-read">Read essay · {article.readMin} min</span>
        </div>
      </div>
    </div>
  );
}

function ArticleCard({ article, onOpen }) {
  const d = new Date(article.date);
  const dateStr = d
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    .toUpperCase();
  return (
    <article className="card" onClick={() => onOpen(article)} data-testid={`journal-card-${article.id}`}>
      <div className="card-top">
        <span className="card-cat">{article.category}</span>
        <span className="card-date">{dateStr}</span>
      </div>
      <h3 className="card-title">{article.title}</h3>
      <p className="card-deck">{article.deck}</p>
      <div className="card-foot">
        <span className="card-author">
          <Avatar initials={article.author.initials} />
          <span>{article.author.name.split(',')[0]}</span>
        </span>
        <span style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <LevelPill level={article.level} />
          <span>{article.readMin} min</span>
        </span>
      </div>
    </article>
  );
}

export default function JournalIndex() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') || 'All';
  const [activeCategory, setActiveCategory] = useState(initialCat);

  useEffect(() => {
    setActiveCategory(searchParams.get('cat') || 'All');
  }, [searchParams]);

  const featured = useMemo(() => (ARTICLES || []).find((a) => a.featured) || ARTICLES[0], []);
  const rest = useMemo(() => (ARTICLES || []).filter((a) => a !== featured), [featured]);
  const filtered = useMemo(
    () => (activeCategory === 'All' ? rest : (rest || []).filter((a) => a.category === activeCategory)),
    [rest, activeCategory]
  );

  // Date-sort filtered essays for category views (newest first).
  const filteredSorted = useMemo(
    () => [...filtered].sort((a, b) => b.date.localeCompare(a.date)),
    [filtered]
  );

  const recent = filteredSorted.slice(0, 4);
  const older = filteredSorted.slice(4, 10);
  // For category views with many essays, group the remainder by year for navigation.
  const remainingByYear = useMemo(() => {
    if (activeCategory === 'All' || filteredSorted.length <= 10) return [];
    const remaining = filteredSorted.slice(4); // everything after the top 4 "Recent"
    const groups = {};
    (remaining || []).forEach((a) => {
      if (!groups[a.year]) groups[a.year] = [];
      groups[a.year].push(a);
    });
    return Object.keys(groups)
      .map((y) => parseInt(y, 10))
      .sort((a, b) => b - a)
      .map((y) => ({ year: y, essays: groups[y] }));
  }, [filteredSorted, activeCategory]);

  const yearCounts = (YEARS || []).map((y) => ({
    year: y,
    count: (ARTICLES || []).filter((a) => a.year === y).length,
    top: (ARTICLES || []).filter((a) => a.year === y)[0],
  }));

  function openArticle(article) {
    navigate(`/journal/${article.id}`);
  }
  function changeCategory(c) {
    setActiveCategory(c);
    if (c === 'All') setSearchParams({});
    else setSearchParams({ cat: c });
  }

  // JSON-LD Blog schema with all 24 BlogPostings for SEO
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'The KGOB Journal',
    description: 'Research-backed editorial essays from Kohari Gonzalez Oneyear & Brown CPAs & Advisors on US tax, wealth advisory, FinCEN compliance, and state-level reporting.',
    url: 'https://kgob.com/journal',
    publisher: {
      '@type': 'AccountingService',
      name: 'Kohari Gonzalez Oneyear & Brown PLLC',
      url: 'https://kgob.com',
    },
    blogPost: (ARTICLES || []).map((a) => ({
      '@type': 'BlogPosting',
      headline: a.title,
      description: a.deck,
      datePublished: a.date,
      author: { '@type': 'Person', name: a.author.name },
      url: `https://kgob.com/journal/${a.id}`,
      image: `https://kgob.com${a.image}`,
      keywords: a.tags.join(', '),
      articleSection: a.category,
    })),
  };

  useJsonLd('journal-blog-index', blogSchema);

  return (
    <div className="kgob-journal-root">
      <Helmet>
        <title>The Journal · Kohari Gonzalez Oneyear &amp; Brown CPAs &amp; Advisors</title>
        <meta
          name="description"
          content="The KGOB Journal: research-backed editorial essays on US tax strategy, wealth planning, tax controversy, FinCEN compliance, and 50-state reporting."
        />
        <link rel="canonical" href="https://kgob.com/journal" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="The KGOB Journal" />
        <meta property="og:title" content="The Journal · KGOB CPAs &amp; Advisors" />
        <meta
          property="og:description"
          content="Research-backed essays from Kohari Gonzalez Oneyear & Brown."
        />
        <meta property="og:url" content="https://kgob.com/journal" />
        <meta property="og:image" content={`https://kgob.com${featured.image}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@kgobcpa" />
        <meta name="twitter:creator" content="@kgobcpa" />
        <link
          href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300;0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,300;1,6..72,400;1,6..72,500&family=Geist:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Masthead />

      <main className="page" data-testid="journal-index-page">
        {(() => {
          const issue = getJournalIssue();
          return (
            <div className="issue">
              <div className="issue-left">{issue.title}</div>
              <div className="issue-title">
                {activeCategory === 'All' ? 'Recent essays across every desk' : activeCategory}
              </div>
              <div className="issue-right">
                {ARTICLES.length} ESSAYS · {Object.keys((ARTICLES || []).reduce((acc, a) => { acc[a.author.name] = 1; return acc; }, {})).length} AUTHORS · ¶ {(ARTICLES || []).reduce((s, a) => s + (a.citations || 0), 0)} CITATIONS
              </div>
            </div>
          );
        })()}

        {featured && activeCategory === 'All' && <FeatureCard article={featured} onOpen={openArticle} />}

        {activeCategory === 'All' ? (
          // ─── ALL view: group articles by category ──────────────────────────
          (CATEGORIES || []).filter((c) => c !== 'All').map((cat) => {
            const catArticles = (rest || [])
              .filter((a) => a.category === cat)
              .sort((a, b) => b.date.localeCompare(a.date))
              .slice(0, 4);
            if (catArticles.length === 0) return null;
            const totalInCat = (ARTICLES || []).filter((a) => a.category === cat).length;
            return (
              <section key={cat} style={{ marginTop: 56 }} data-testid={`journal-section-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="row-meta">
                  <h2>
                    {cat}
                    <span
                      style={{
                        color: 'var(--brass)',
                        fontStyle: 'normal',
                        fontSize: 14,
                        fontFamily: 'var(--ui)',
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        marginLeft: 14,
                        fontWeight: 500,
                      }}
                    >
                      · {totalInCat} ESSAYS
                    </span>
                  </h2>
                  <span
                    className="mono"
                    onClick={() => changeCategory(cat)}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    data-testid={`journal-section-viewall-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    View all {totalInCat} →
                  </span>
                </div>
                <div className="cards">
                  {(catArticles || []).map((a) => (
                    <ArticleCard key={a.id} article={a} onOpen={openArticle} />
                  ))}
                </div>
              </section>
            );
          })
        ) : (
          // ─── Category-filtered view: scales by grouping by year as essays accumulate ──
          <>
            <div className="row-meta">
              <h2>
                Recent essays
                <span
                  style={{
                    color: 'var(--brass)',
                    fontStyle: 'normal',
                    fontSize: 16,
                    fontFamily: 'var(--ui)',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    marginLeft: 12,
                  }}
                >
                  · {activeCategory}
                </span>
              </h2>
              <span className="mono">{filteredSorted.length} essays</span>
            </div>
            <div className="cards">
              {(recent || []).map((a) => (
                <ArticleCard key={a.id} article={a} onOpen={openArticle} />
              ))}
            </div>

            {/* Compact list when the section is small (≤ 10 essays). */}
            {filteredSorted.length <= 10 && older.length > 0 && (
              <>
                <div className="row-meta" style={{ marginTop: 56 }}>
                  <h2>Earlier in this section</h2>
                  <span className="mono">{older.length} more</span>
                </div>
                <div className="cards three-up">
                  {(older || []).map((a) => (
                    <ArticleCard key={a.id} article={a} onOpen={openArticle} />
                  ))}
                </div>
              </>
            )}

            {/* Year-grouped archive when the section has scaled past 10 essays. */}
            {filteredSorted.length > 10 && remainingByYear.length > 0 && (
              <>
                <div className="row-meta" style={{ marginTop: 56 }}>
                  <h2>Earlier in this section, by year</h2>
                  <span className="mono">{filteredSorted.length - 4} more</span>
                </div>
                {(remainingByYear || []).map((g) => (
                  <section key={g.year} style={{ marginTop: 36 }} data-testid={`journal-cat-year-${g.year}`}>
                    <div
                      className="row-meta"
                      style={{
                        borderBottom: '1px solid var(--rule)',
                        paddingBottom: 10,
                        marginBottom: 18,
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 28,
                          fontFamily: 'var(--mono)',
                          fontStyle: 'normal',
                          color: 'var(--ink)',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {g.year}
                      </h2>
                      <span className="mono">{g.essays.length} essay{g.essays.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="cards three-up">
                      {(g.essays || []).map((a) => (
                        <ArticleCard key={a.id} article={a} onOpen={openArticle} />
                      ))}
                    </div>
                  </section>
                ))}
              </>
            )}
          </>
        )}

        <section className="archive-teaser">
          <div className="row-meta">
            <h2>From the archive</h2>
            <span
              className="mono"
              onClick={() => navigate('/journal/archive')}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              data-testid="journal-browse-all-years"
            >
              Browse all years →
            </span>
          </div>
          <div className="archive-grid">
            {(yearCounts || []).map((yc) => (
              <div
                className="year-card"
                key={yc.year}
                onClick={() => navigate('/journal/archive')}
                data-testid={`journal-year-card-${yc.year}`}
              >
                <div className="num">{yc.year}</div>
                <div className="sub">{yc.count} essays</div>
                {yc.top && <div className="top">"{yc.top.title}"</div>}
              </div>
            ))}
          </div>
        </section>
      </main>

      <Colophon />
    </div>
  );
}
